import { addDays } from 'date-fns'
import { useInformacoesPuerperioQuery } from 'graphql/hooks.generated'
import { useMemo } from 'react'
import { isBetween } from 'util/date/compare'
import { toDate } from 'util/date/formatDate'

import {
  AcompanhamentoPuerperioInformacoes,
  DIAS_GESTACAO_LIMITE,
  DIAS_MODULO_ATIVO,
  DIAS_PUERPERIO,
} from '../model-acompanhamentoPuerperio'

export interface UseAcompanhamentoPuerperioResult {
  loading: boolean
  data: AcompanhamentoPuerperioInformacoes | null
}

export const useAcompanhamentoPuerperio = (
  prontuarioId: ID,
  dataReferencia: Instant
): UseAcompanhamentoPuerperioResult => {
  const { data, loading } = useInformacoesPuerperioQuery({
    variables: { prontuarioId },
    skip: !prontuarioId,
    fetchPolicy: 'cache-and-network',
  })

  const informacoesPuerperio = useMemo((): AcompanhamentoPuerperioInformacoes | null => {
    const informacoesPuerperio = data?.informacoesPuerperio

    if (!informacoesPuerperio) return null

    const dataDesfecho = toDate(informacoesPuerperio.preNatal?.dataDesfecho)

    if (dataDesfecho) {
      const dataFimPuerperio = addDays(dataDesfecho, DIAS_PUERPERIO)

      return {
        duracaoPuerperio: {
          startDate: dataDesfecho,
          endDate: dataFimPuerperio,
        },
        dataInicioGestacao: informacoesPuerperio.dataInicioGestacao,
        puerperioAtivo: isBetween(toDate(dataReferencia), dataDesfecho, dataFimPuerperio),
        acompanhamentoAtivo: isBetween(toDate(dataReferencia), dataDesfecho, addDays(dataDesfecho, DIAS_MODULO_ATIVO)),
        dataDesfechoGestacao: dataDesfecho,
        fonteIdadeGestacional: informacoesPuerperio?.preNatal?.fonteIdadeGestacional,
      }
    }

    const dataInicioGestacao = toDate(informacoesPuerperio.dataInicioGestacao)

    if (dataInicioGestacao) {
      const dpp = addDays(dataInicioGestacao, DIAS_GESTACAO_LIMITE)
      const dataFimPuerperio = addDays(dpp, DIAS_PUERPERIO)

      return {
        duracaoPuerperio: {
          startDate: dpp,
          endDate: dataFimPuerperio,
        },
        dataInicioGestacao: informacoesPuerperio.dataInicioGestacao,
        puerperioAtivo: isBetween(toDate(dataReferencia), dpp, dataFimPuerperio),
        acompanhamentoAtivo: isBetween(toDate(dataReferencia), dpp, addDays(dpp, DIAS_MODULO_ATIVO)),
        fonteIdadeGestacional: informacoesPuerperio?.preNatal?.fonteIdadeGestacional,
      }
    }

    return null
  }, [data, dataReferencia])

  return {
    loading,
    data: informacoesPuerperio,
  }
}
